<template>
  <Teleport to="body">
    <div
      v-if="showModal"
      class="fixed inset-0 z-[1000] flex items-center justify-center p-5 bg-black/60 backdrop-blur-sm"
      @click.self="closeModal"
    >
      <div
        class="relative w-full max-w-md rounded-2xl p-7 animate-fadeIn backdrop-blur-2xl border shadow-2xl"
        :class="
          isLight
            ? 'bg-[#e4f4f0]/95 border-[#d0ede8]/60 shadow-[#0f1f1c]/20'
            : 'bg-[#0f1f1c]/95 border-[#1a9b8a]/25 shadow-black/60'
        "
      >
        <!-- Close -->
        <button
          @click="closeModal"
          class="absolute top-4 right-4 w-7 h-7 rounded-full flex items-center justify-center border transition-all duration-200"
          :class="
            isLight
              ? 'bg-white border-[#d0ede8] text-[#6b8f89] hover:border-red-300 hover:text-red-500'
              : 'bg-white/6 border-white/10 text-[#a8d5cc] hover:border-red-500/40 hover:text-red-400'
          "
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <!-- Category pill -->
        <div class="mb-3">
          <span
            class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase border"
            :class="
              isLight
                ? 'text-[#0d6b5e] bg-[#1a9b8a]/10 border-[#1a9b8a]/30'
                : 'text-[#a8d5cc] bg-[#1a9b8a]/10 border-[#1a9b8a]/25'
            "
          >
            {{ $t('mapModal.eventBadge') }}
          </span>
        </div>

        <!-- Title -->
        <h2
          class="font-display font-black text-xl leading-tight tracking-tight mb-4"
          :class="isLight ? 'text-[#0f1f1c]' : 'text-white'"
        >
          {{ selectedEvent.Title }}
        </h2>

        <!-- Image -->
        <div
          v-if="selectedEvent.ImageURL"
          class="rounded-xl overflow-hidden mb-4 border"
          :class="isLight ? 'border-[#d0ede8]' : 'border-[#1a9b8a]/20'"
        >
          <img :src="selectedEvent.ImageURL" :alt="selectedEvent.Title" class="w-full object-cover max-h-56" />
        </div>

        <!-- Description -->
        <p
          v-if="selectedEvent.Description"
          class="text-sm leading-relaxed mb-3"
          :class="isLight ? 'text-[#6b8f89]' : 'text-white/55'"
        >
          {{ selectedEvent.Description }}
        </p>

        <!-- Tags -->
        <div v-if="selectedEvent.tags" class="flex flex-wrap gap-1.5 mb-4">
          <span
            v-for="tag in selectedEvent.tags"
            :key="tag"
            class="text-[11px] font-medium transition-colors duration-200"
            :class="isLight ? 'text-[#1a9b8a] hover:text-[#0d6b5e]' : 'text-[#a8d5cc]/70 hover:text-[#a8d5cc]'"
          >
            {{ tag }}
          </span>
        </div>

        <div class="h-px mb-5" :class="isLight ? 'bg-[#d0ede8]' : 'bg-white/10'"></div>

        <!-- Actions -->
        <div class="flex gap-3">
          <a
            :href="`https://www.google.com/maps?q=${selectedEvent.Latitude},${selectedEvent.Longitude}`"
            target="_blank"
            class="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium no-underline border transition-all duration-200"
            :class="
              isLight
                ? 'bg-white border-[#d0ede8] text-[#0d6b5e] hover:bg-[#e4f4f0] hover:border-[#1a9b8a]/40'
                : 'bg-white/5 border-white/10 text-[#a8d5cc] hover:border-[#1a9b8a]/40 hover:text-white'
            "
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
            </svg>
            {{ $t('mapModal.directions') }}
          </a>
          <a
            :href="`/event/${selectedEvent.id}`"
            class="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold no-underline transition-all duration-200 text-white hover:-translate-y-px bg-[#1a9b8a] hover:bg-[#0d6b5e]"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" />
            </svg>
            {{ $t('mapModal.claimSpot') }}
          </a>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
export default {
  name: "MapEventModelComponent",
  props: { showModal: Boolean, selectedEvent: Object, isLight: Boolean },
  emits: ["close"],
  methods: { closeModal() { this.$emit("close"); } },
};
</script>